package br.com.texeratx.boardgametimeapp.ui.screens.players.detail

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.EmojiEvents
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.Groups
import androidx.compose.material.icons.filled.Insights
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.InfoCard
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.InfoRow
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchListItemUI
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDate
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDateTime
import br.com.texeratx.boardgametimeapp.ui.utils.formatPhoneForDisplay
import br.com.texeratx.boardgametimeapp.ui.utils.minutesToHourMin
import cafe.adriel.voyager.navigator.LocalNavigator
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors

@Composable
fun PlayerDetailUI(
    player: PlayerDetailUIModel,
    contentPadding: PaddingValues = PaddingValues(0.dp),
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit,
    showDeleteConfirmation: Boolean,
    isDeleting: Boolean,
    onDismissDeleteConfirmation: () -> Unit,
    onConfirmDelete: () -> Unit,
    onGameClick: (PlayerOwnedGameUI) -> Unit,
    onMatchClick: (MatchListItemUI) -> Unit
) {
    val S = LocalStrings.current
    val navigator = LocalNavigator.current

    var statsExpanded by remember { mutableStateOf(true) }
    var matchupsExpanded by remember { mutableStateOf(false) }
    var winsByGameExpanded by remember { mutableStateOf(false) }
    var gamesExpanded by remember { mutableStateOf(false) }
    var matchesExpanded by remember { mutableStateOf(false) }
    var selectedMatchupPeriod by remember { mutableStateOf(MatchupPeriodSelection.Total) }
    val wonGames = player.winsByGame.filter { it.wins > 0 }
    val selectedMatchupPeriodData = when (selectedMatchupPeriod) {
        MatchupPeriodSelection.Total -> player.matchupStats?.total
        MatchupPeriodSelection.CurrentYear -> player.matchupStats?.currentYear
    }

    if (showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = onDismissDeleteConfirmation,
            title = { Text(S.deletePlayer) },
            text = { Text(S.deletePlayerConfirm) },
            confirmButton = {
                TextButton(
                    onClick = onConfirmDelete,
                    enabled = !isDeleting
                ) {
                    Text(S.delete)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = onDismissDeleteConfirmation,
                    enabled = !isDeleting
                ) {
                    Text(S.cancel)
                }
            }
        )
    }

    Column(modifier = Modifier.fillMaxSize()) {

        PlayerDetailTopBar(
            title = S.playerDetailTitle,
            onBack = { navigator?.pop() },
            onEdit = onEditClick,
            onDelete = onDeleteClick
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(bottom = contentPadding.calculateBottomPadding())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            if (!player.imageUrl.isNullOrBlank()) {
                PlayerAvatar(
                    imageUrl = player.imageUrl,
                    contentDescription = player.name,
                    size = 120.dp,
                    modifier = Modifier
                        .size(120.dp)
                        .clip(CircleShape)
                        .align(Alignment.CenterHorizontally)
                )
            }

            InfoCard {
                InfoRow(S.labelName, player.name)
                InfoRow(S.labelNickname, player.nickname)
                InfoRow(S.labelBirthday, apiToUiDate(player.birthday))
                InfoRow(S.labelPhone, formatPhoneForDisplay(player.phone))
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionPlayerStatistics,
                    icon = Icons.Default.Insights,
                    expanded = statsExpanded,
                    onToggle = { statsExpanded = !statsExpanded }
                )

                if (statsExpanded) {
                    Spacer(Modifier.height(8.dp))
                    InfoRow(S.playerStatWins, player.stats.wins.toString())
                    InfoRow(S.playerStatParticipations, player.stats.participations.toString())
                    InfoRow(S.playerStatHoursPlayed, minutesToHourMin(player.stats.minutesPlayed))
                    InfoRow(S.playerStatWinsYear, player.stats.winsCurrentYear.toString())
                    InfoRow(S.playerStatParticipationsYear, player.stats.participationsCurrentYear.toString())
                    InfoRow(S.playerStatHoursPlayedYear, minutesToHourMin(player.stats.minutesPlayedCurrentYear))
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionMatchups,
                    icon = Icons.Default.Groups,
                    expanded = matchupsExpanded,
                    onToggle = { matchupsExpanded = !matchupsExpanded }
                )

                if (matchupsExpanded) {
                    Spacer(Modifier.height(8.dp))

                    MatchupPeriodToggle(
                        selected = selectedMatchupPeriod,
                        onSelected = { selectedMatchupPeriod = it }
                    )

                    Spacer(Modifier.height(10.dp))

                    val matchups = selectedMatchupPeriodData
                    if (matchups == null || matchups.topRivals.isEmpty()) {
                        EmptySectionText(S.emptyMatchups)
                    } else {
                        MatchupHighlights(
                            highlights = matchups.highlights
                        )

                        Spacer(Modifier.height(12.dp))

                        Text(
                            text = S.matchupTopRivals,
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.Bold
                        )

                        Spacer(Modifier.height(8.dp))

                        matchups.topRivals.forEachIndexed { index, rival ->
                            RivalMatchupCard(rival = rival)
                            if (index != matchups.topRivals.lastIndex) {
                                Spacer(Modifier.height(10.dp))
                            }
                        }
                    }
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = "${S.sectionWinsByGame} (${wonGames.size})",
                    icon = Icons.Default.EmojiEvents,
                    expanded = winsByGameExpanded,
                    onToggle = { winsByGameExpanded = !winsByGameExpanded }
                )

                if (winsByGameExpanded) {
                    Spacer(Modifier.height(8.dp))

                    if (wonGames.isEmpty()) {
                        EmptySectionText(S.emptyWinsByGame)
                    } else {
                        wonGames.forEachIndexed { index, game ->
                            WonGameRow(
                                game = game,
                                winsLabel = S.playerGameWinsFormat(game.wins),
                                onClick = {
                                    onGameClick(
                                        PlayerOwnedGameUI(
                                            id = game.gameId,
                                            name = game.gameName,
                                            imageUrl = game.gameImageUrl
                                        )
                                    )
                                }
                            )
                            if (index != wonGames.lastIndex) {
                                Spacer(Modifier.height(10.dp))
                            }
                        }
                    }
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = "${S.sectionGamesOwned} (${player.gamesOwned.size})",
                    icon = Icons.Default.Groups,
                    expanded = gamesExpanded,
                    onToggle = { gamesExpanded = !gamesExpanded }
                )

                if (gamesExpanded) {
                    Spacer(Modifier.height(8.dp))

                    if (player.gamesOwned.isEmpty()) {
                        EmptySectionText(S.emptyGamesOwned)
                    } else {
                        player.gamesOwned.forEachIndexed { index, game ->
                            OwnedGameRow(
                                game = game,
                                onClick = { onGameClick(game) }
                            )
                            if (index != player.gamesOwned.lastIndex) {
                                Spacer(Modifier.height(10.dp))
                            }
                        }
                    }
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionRecentMatches,
                    icon = Icons.Default.Schedule,
                    expanded = matchesExpanded,
                    onToggle = { matchesExpanded = !matchesExpanded }
                )

                if (matchesExpanded) {
                    Spacer(Modifier.height(8.dp))

                    if (player.recentMatches.isEmpty()) {
                        EmptySectionText(S.emptyRecentMatches)
                    } else {
                        player.recentMatches.forEachIndexed { index, match ->
                            PlayerRecentMatchCard(
                                match = match,
                                onClick = { onMatchClick(match) }
                            )
                            if (index != player.recentMatches.lastIndex) {
                                Spacer(Modifier.height(10.dp))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ExpandableSectionHeader(
    title: String,
    icon: ImageVector,
    expanded: Boolean,
    onToggle: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onToggle() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier.weight(1f),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary
            )
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
        }

        Icon(
            imageVector = if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary
        )
    }
}

@Composable
private fun OwnedGameRow(
    game: PlayerOwnedGameUI,
    onClick: () -> Unit
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(12.dp)
            )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (!game.imageUrl.isNullOrBlank()) {
                GameThumb(
                    imageUrl = game.imageUrl,
                    contentDescription = game.name,
                    size = 56.dp,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier
                        .size(56.dp)
                        .clip(RoundedCornerShape(10.dp))
                )
            } else {
                Box(
                    modifier = Modifier
                        .size(56.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .background(MaterialTheme.colorScheme.surfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = game.name.firstOrNull()?.uppercase() ?: "?",
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            Spacer(Modifier.width(12.dp))

            Text(
                text = game.name,
                style = MaterialTheme.typography.bodyLarge,
                fontWeight = FontWeight.Medium,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun WonGameRow(
    game: PlayerGameWinUI,
    winsLabel: String,
    onClick: () -> Unit
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(12.dp)
            )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (!game.gameImageUrl.isNullOrBlank()) {
                GameThumb(
                    imageUrl = game.gameImageUrl,
                    contentDescription = game.gameName,
                    size = 56.dp,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier
                        .size(56.dp)
                        .clip(RoundedCornerShape(10.dp))
                )
            } else {
                Box(
                    modifier = Modifier
                        .size(56.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .background(MaterialTheme.colorScheme.surfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = game.gameName.firstOrNull()?.uppercase() ?: "?",
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            Spacer(Modifier.width(12.dp))

            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Text(
                    text = game.gameName,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Medium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                DetailChip(winsLabel)
            }
        }
    }
}

@Composable
private fun PlayerRecentMatchCard(
    match: MatchListItemUI,
    onClick: () -> Unit
) {
    val S = LocalStrings.current
    val orderedPlayers = match.players.sortedByDescending { it.points }

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(16.dp)
            )
            .clickable { onClick() }
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "${match.game.name} - ${apiToUiDateTime(match.date)}",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = S.matchDurationDisplay(minutesToHourMin(match.duration)),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                if (match.expansionIds.isNotEmpty()) {
                    DetailChip(text = S.expansionCountFormat(match.expansionIds.size))
                }
            }

            orderedPlayers.forEachIndexed { index, player ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    PlayerAvatar(
                        imageUrl = player.imageUrl,
                        contentDescription = player.name,
                        size = 34.dp,
                        modifier = Modifier.size(34.dp)
                    )

                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = player.nickname ?: player.name,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = if (index == 0) FontWeight.Bold else FontWeight.Medium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Text(
                            text = player.name,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }

                    Text(
                        text = S.pointsFormat(player.points.toInt()),
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = if (index == 0) FontWeight.Bold else FontWeight.Normal,
                        color = if (index == 0) {
                            MaterialTheme.colorScheme.primary
                        } else {
                            MaterialTheme.colorScheme.onSurface
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun DetailChip(text: String) {
    Surface(
        shape = RoundedCornerShape(999.dp),
        color = MaterialTheme.colorScheme.secondaryContainer
    ) {
        Text(
            text = text,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSecondaryContainer,
            fontWeight = FontWeight.SemiBold
        )
    }
}

@Composable
private fun EmptySectionText(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
}

private enum class MatchupPeriodSelection {
    Total,
    CurrentYear
}

@Composable
private fun MatchupPeriodToggle(
    selected: MatchupPeriodSelection,
    onSelected: (MatchupPeriodSelection) -> Unit
) {
    val S = LocalStrings.current
    Surface(
        shape = RoundedCornerShape(14.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
        modifier = Modifier.border(
            width = 1.dp,
            color = MaterialTheme.colorScheme.outlineVariant,
            shape = RoundedCornerShape(14.dp)
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Text(
                text = S.sectionMatchups,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                fontWeight = FontWeight.Medium,
                modifier = Modifier.padding(horizontal = 6.dp)
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                MatchupPeriodButton(
                    text = S.matchupPeriodTotal,
                    selected = selected == MatchupPeriodSelection.Total,
                    onClick = { onSelected(MatchupPeriodSelection.Total) },
                    modifier = Modifier.weight(1f)
                )
                MatchupPeriodButton(
                    text = S.matchupPeriodCurrentYear,
                    selected = selected == MatchupPeriodSelection.CurrentYear,
                    onClick = { onSelected(MatchupPeriodSelection.CurrentYear) },
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}

@Composable
private fun MatchupPeriodButton(
    text: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Surface(
        modifier = modifier.clickable { onClick() },
        shape = RoundedCornerShape(10.dp),
        color = if (selected) {
            MaterialTheme.colorScheme.primary
        } else {
            MaterialTheme.colorScheme.surface
        },
        tonalElevation = if (selected) 2.dp else 0.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 10.dp, vertical = 9.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = text,
                style = MaterialTheme.typography.labelLarge,
                color = if (selected) {
                    MaterialTheme.colorScheme.onPrimary
                } else {
                    MaterialTheme.colorScheme.onSurfaceVariant
                },
                fontWeight = FontWeight.SemiBold
            )
        }
    }
}

@Composable
private fun MatchupHighlights(highlights: MatchupHighlightsUI) {
    val S = LocalStrings.current
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        MatchupHighlightRow(
            title = S.matchupMostDefeated,
            rival = highlights.mostDefeated
        )
        MatchupHighlightRow(
            title = S.matchupMostLostTo,
            rival = highlights.mostLostTo
        )
        MatchupHighlightRow(
            title = S.matchupMostPlayedTogether,
            rival = highlights.mostPlayedTogether
        )
    }
}

@Composable
private fun MatchupHighlightRow(
    title: String,
    rival: RivalMatchupUI?
) {
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surfaceVariant
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 8.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Medium
            )
            Text(
                text = rival?.let { it.opponentNickname ?: it.opponentName } ?: "-",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun RivalMatchupCard(rival: RivalMatchupUI) {
    val S = LocalStrings.current
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(12.dp)
            )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                PlayerAvatar(
                    imageUrl = rival.opponentImageUrl,
                    contentDescription = rival.opponentName,
                    size = 38.dp,
                    modifier = Modifier.size(38.dp)
                )
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = rival.opponentNickname ?: rival.opponentName,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        text = rival.opponentName,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                DetailChip(text = "${rival.matchesTogether}x")
            }

            Text(
                text = S.matchupRecordFormat(rival.wins, rival.losses),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Medium
            )
            Text(
                text = S.matchupWinRateFormat(rival.winRatePct),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = S.matchupDominanceFormat(rival.dominance),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            rival.lastMatchDate?.let {
                Text(
                    text = S.matchupLastMatchFormat(apiToUiDateTime(it)),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            rival.bestGameAgainst?.let {
                Text(
                    text = S.matchupBestGameFormat(it.gameName),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            rival.worstGameAgainst?.let {
                Text(
                    text = S.matchupWorstGameFormat(it.gameName),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlayerDetailTopBar(
    title: String,
    onBack: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    val S = LocalStrings.current
    TopAppBar(
        title = {
            Text(
                text = title,
                fontWeight = FontWeight.Bold
            )
        },
        navigationIcon = {
            Icon(
                imageVector = Icons.Default.ArrowBack,
                contentDescription = S.back,
                modifier = Modifier
                    .padding(start = 12.dp)
                    .clickable { onBack() }
            )
        },
        actions = {
            IconButton(onClick = onEdit) {
                Icon(
                    imageVector = Icons.Default.Edit,
                    contentDescription = S.edit
                )
            }
            IconButton(
                onClick = onDelete,
                modifier = Modifier.padding(end = 4.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Delete,
                    contentDescription = S.delete
                )
            }
        },
        colors = AppTopBarColors()
    )
}
